/**
 * Modal showing patient diagnosis codes and primary Dx selection with save to backend.
 * @module ClinicalDataTable
 */

import { useSelector } from "react-redux";
import { Modal } from "risa-oasis-ui_v2";
import { ModalId } from "../../../enums/modalId";
import { RootState } from "../../../redux/store/store";
import "./clinicalDataTable.scss";
import { DxCodeChangeReasonFields } from "./components/DxCodeChangeReasonFields";
import { ManualDxEntryFields } from "./components/ManualDxEntryFields";
import { PatientDxCodesSection } from "./components/PatientDxCodesSection";
import { PrimaryDxSelect } from "./components/PrimaryDxSelect";
import { CLINICAL_DATA_TABLE_COLUMNS } from "./constants";
import { useClinicalDataTable } from "./hooks/useClinicalDataTable";
import { useClinicalDataTableSave } from "./hooks/useClinicalDataTableSave";
import { ClinicalDataTableProps } from "./types";

export type { TableData } from "./types";

/**
 * Displays ICD rows, primary Dx selector, and persists selection via save utils.
 *
 * @param props - Clinical data, screen name, optional parent `onSave`, and `onClose`.
 */
const ClinicalDataTable = (props: ClinicalDataTableProps) => {
  const { data: healthFacilityData } = useSelector(
    (state: RootState) => state.singleHealthFacilityDetails,
  );
  const table = useClinicalDataTable(props);
  const save = useClinicalDataTableSave({ props, ...table });

  const primarySources = Array.from(
    new Set(
      (
        table.submissionData?.submission?.primary_diagnosis_details ?? []
      ).flatMap((diagnosis) => diagnosis.source ?? []),
    ),
  );

  return (
    <Modal
      dialogId={ModalId.CLINICAL_DATA_TABLE}
      title="Dx Code"
      onSave={save.onSave}
      saveButtonText={save.isSaving ? "Saving..." : "Save"}
      cancelText="Cancel"
      onClose={props.onClose}
      showSingleButton={false}
      heightPercentage={80}
      disableSave={
        save.isSaving ||
        table.isManualEntryMissing ||
        (healthFacilityData?.feature_config?.need_reason_for_dx_change &&
          (table.isReasonMissing || table.isSourceMissing))
      }
      hideFooter={false}
    >
      <div className="flex flex-col items-start gap-6 self-stretch">
        <PrimaryDxSelect
          options={table.dropdownOpts}
          value={table.selectedPrimaryCode || table.defaultPrimaryCode}
          onOptionChange={table.handlePrimaryCodeChange}
        />
        {table.isManualEntry && (
          <ManualDxEntryFields
            manualPrimaryCode={table.manualPrimaryCode}
            description={table.selectedPrimaryCodeDescription}
            isManualEntryMissing={table.isManualEntryMissing}
            onManualCodeChange={table.handleManualCodeChange}
          />
        )}
        {table.hasPrimaryDxChanged &&
          healthFacilityData?.feature_config?.need_reason_for_dx_change && (
            <div className="dx_code_changes_reason--container w-full">
              <DxCodeChangeReasonFields
                userReason={table.userReason}
                userSource={table.userSource}
                isReasonMissing={table.isReasonMissing}
                isSourceMissing={table.isSourceMissing}
                onReasonChange={table.setUserReason}
                onSourceChange={table.setUserSource}
                onOtherReasonChange={table.setOtherReason}
                onOtherSourceChange={table.setOtherSource}
              />
            </div>
          )}
        <PatientDxCodesSection
          category={healthFacilityData?.category}
          tableData={table.tableData}
          tableHeaders={CLINICAL_DATA_TABLE_COLUMNS}
          getRowClassName={table.getRowClassName}
          primarySources={primarySources}
        />
      </div>
    </Modal>
  );
};

export default ClinicalDataTable;
